import React from 'react';
import Seo from '../common/Seo';
import ShareButton from '../common/ShareButton';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaPhone, FaCheckCircle, FaClock, FaShieldAlt, FaUserMd } from 'react-icons/fa';
import { COMPANY_INFO, COVERAGE_AREAS } from '../../constants';
import { BLOG_POSTS } from '../../data/blog';

// Arquitectura de topic cluster: todas las landing pages de servicio se enlazan entre sí
const SERVICE_PAGES = [
  { path: '/enfermeria-24-horas', label: 'Enfermería 24 Horas' },
  { path: '/cuidados-geriatricos', label: 'Cuidados Geriátricos' },
  { path: '/atencion-postoperatoria', label: 'Atención Postoperatoria' },
  { path: '/enfermeria-zapopan', label: 'Enfermería en Zapopan' },
  { path: '/enfermeria-tlaquepaque', label: 'Enfermería en Tlaquepaque' },
  { path: '/servicios-expres', label: 'Servicios Exprés' }
];

/**
 * Componente reutilizable para landing pages de servicios específicos
 */
const ServiceLandingPage = ({
  title,
  metaDescription,
  heroTitle,
  heroSubtitle,
  description,
  benefits,
  features,
  faqs,
  ctaText = "Solicitar Información",
  image,
  relatedBlogSlugs = []
}) => {
  const handleWhatsApp = () => {
    const message = `Hola, me interesa obtener información sobre ${title}. ¿Podrían ayudarme?`;
    window.open(
      `https://wa.me/${COMPANY_INFO.whatsapp}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const handleCall = () => {
    window.open(`tel:${COMPANY_INFO.phone}`, '_self');
  };

  const pageUrl = `https://geriatriaserena.com${window.location.pathname}`;
  const relatedServices = SERVICE_PAGES.filter((s) => s.path !== window.location.pathname);
  const relatedPosts = BLOG_POSTS.filter((p) => relatedBlogSlugs.includes(p.slug));

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": title,
      "description": metaDescription,
      "url": pageUrl,
      "areaServed": COVERAGE_AREAS,
      "provider": {
        "@type": "MedicalBusiness",
        "name": "Geriatría Serena",
        "telephone": "+52-33-2545-6522",
        "url": "https://geriatriaserena.com"
      }
    },
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
          }))
        }
      : null
  ].filter(Boolean);

  return (
    <>
      <Seo
        title={`${title} | Geriatría Serena - Guadalajara`}
        description={metaDescription}
        keywords={`${title}, enfermería a domicilio, Guadalajara, Zapopan, cuidado adultos mayores`}
        canonical={pageUrl}
        jsonLd={jsonLd}
        meta={[
          { property: 'og:title', content: `${title} | Geriatría Serena` },
          { property: 'og:description', content: metaDescription },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: pageUrl }
        ]}
      />

      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pt-32 pb-20">
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-10">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleWhatsApp}
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all transform hover:scale-105"
              >
                <FaWhatsapp className="text-2xl" />
                {ctaText}
              </button>
              <button
                onClick={handleCall}
                className="bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-gray-100 transition-all"
              >
                <FaPhone />
                Llamar Ahora
              </button>
            </div>
            <div className="mt-6 flex justify-center">
              <ShareButton
                title={heroTitle}
                text={heroSubtitle}
                url={pageUrl}
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-100 hover:text-white transition-colors"
              />
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <FaClock className="text-3xl mb-2" />
                <span className="font-semibold">24/7 Disponible</span>
              </div>
              <div className="flex flex-col items-center">
                <FaShieldAlt className="text-3xl mb-2" />
                <span className="font-semibold">Personal Certificado</span>
              </div>
              <div className="flex flex-col items-center">
                <FaUserMd className="text-3xl mb-2" />
                <span className="font-semibold">+5 Años Experiencia</span>
              </div>
              <div className="flex flex-col items-center">
                <FaCheckCircle className="text-3xl mb-2" />
                <span className="font-semibold">Garantía de Servicio</span>
              </div>
            </div>
          </div>
        </section>

        {/* Description Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  ¿Qué incluye nuestro servicio?
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {description}
                </p>

                <button
                  onClick={handleWhatsApp}
                  className="mb-8 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-3 transition-all"
                >
                  <FaWhatsapp className="text-xl" />
                  {ctaText}
                </button>

                {benefits && benefits.length > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-8">
                    <h3 className="font-bold text-blue-900 mb-4">
                      En resumen: lo esencial de este servicio
                    </h3>
                    <ul className="space-y-2">
                      {benefits.slice(0, 5).map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <FaCheckCircle className="text-blue-600 mt-1 flex-shrink-0" />
                          <span className="text-blue-900">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {image && (
                <div className="relative">
                  <img
                    src={image}
                    alt={title}
                    className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Características del Servicio
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <FaCheckCircle className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Áreas de Cobertura
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Brindamos este servicio en toda el área metropolitana de Guadalajara
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {COVERAGE_AREAS.map((area, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-800 px-6 py-3 rounded-full font-medium"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        {faqs && faqs.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                Preguntas Frecuentes
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="bg-white rounded-xl shadow-md p-6 group"
                  >
                    <summary className="font-bold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                      {faq.question}
                      <span className="text-blue-600 group-open:rotate-180 transition-transform">
                        ▼
                      </span>
                    </summary>
                    <p className="mt-4 text-gray-600">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Interlinkeado: topic cluster de servicios y blog */}
        <section className="py-16 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Otros servicios que te pueden interesar</h2>
              <ul className="space-y-3">
                {relatedServices.map((service) => (
                  <li key={service.path}>
                    <Link to={service.path} className="text-blue-600 font-medium hover:underline flex items-center gap-2">
                      <FaCheckCircle className="text-blue-400 flex-shrink-0" />
                      {service.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/services" className="text-blue-600 font-medium hover:underline flex items-center gap-2">
                    <FaCheckCircle className="text-blue-400 flex-shrink-0" />
                    Ver catálogo completo de servicios
                  </Link>
                </li>
              </ul>
            </div>
            {relatedPosts.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Artículos relacionados</h2>
                <ul className="space-y-3">
                  {relatedPosts.map((post) => (
                    <li key={post.slug}>
                      <Link to={`/blog/${post.slug}`} className="text-blue-600 font-medium hover:underline flex items-center gap-2">
                        <FaCheckCircle className="text-blue-400 flex-shrink-0" />
                        {post.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              ¿Listo para comenzar?
            </h2>
            <p className="text-xl text-blue-100 mb-10">
              Contáctanos hoy y recibe una cotización personalizada sin compromiso
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleWhatsApp}
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3"
              >
                <FaWhatsapp className="text-2xl" />
                WhatsApp
              </button>
              <Link
                to="/contact"
                className="bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all text-center"
              >
                Formulario de Contacto
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ServiceLandingPage;
